import { Suspense, lazy, useEffect } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
// components
import Layout from '../layouts';
import StyledCircularProgress from '../components/StyledCircularProgress';
import { IdentityPermission, OrderPermission, QuotePermission, InstallationPermission, InvoicePermission, DiscrepancyPermission } from '../Common/enums';
import { AuthGuard } from '../guards/AuthGuard';
import AdminPage from '../pages/generalPages/AdminPage';
import { getAppSettings } from '../Common/Utils/localStorageHelper';
// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => (
  <Suspense
    fallback={
      <StyledCircularProgress />
    }
  >
    <Component {...props} />
  </Suspense>
);

const Profile = () => {
  const appSettings = getAppSettings();

  useEffect(() => {
    window.location.href = `${appSettings.authority}/Manage/EditProfile`;
  }, []);

  return (<></>);
};

const ChangePassword = () => {
  const appSettings = getAppSettings();

  useEffect(() => {
    window.location.href = `${appSettings.authority}/Manage/Security`;
  }, []);

  return (<></>);
};

export default function Router() {

  let routes = [
    {
      path: '/',
      element:
        <AuthGuard>
          <Layout><LandingPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/activity-log',
      element:
        <AuthGuard>
          <Layout><ActivityLog /></Layout>
        </AuthGuard>
    },
    {
      path: '/orderlist',
      element:
        <AuthGuard
          requiredPermission={{
            permission: OrderPermission.view_orders,
            permissionType: 'orderPermissions'
          }}
        >
          <Layout><OrderListPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/admin',
      element: <AdminPage />    
    },
    {
      path: '/details',
      element:
        <AuthGuard
          requiredPermission={{
            permission: OrderPermission.view_orders,
            permissionType: 'orderPermissions'
          }}
        >
          <Layout><OrderDetails /></Layout>
        </AuthGuard>
    },
    {
      path: '/booking',
      element:
        <AuthGuard
          requiredPermission={{
            permission: OrderPermission.create_orders,
            permissionType: 'orderPermissions'
          }}
        >
          <Layout><Booking /></Layout>
        </AuthGuard>
    },
    {
      path: '/assetList',
      element:
        <AuthGuard
          requiredPermission={{
            permission: InstallationPermission.view_installations,
            permissionType: 'sitePermissions' //This is still called 'sitePermissions' in the userInformation endpoint, but it is actually installation permission
          }}
        >
          <Layout><AssetList /></Layout>
        </AuthGuard>
    },
    {
      path: '/assetDetails',
      element:
        <AuthGuard
          requiredPermission={{
            permission: InstallationPermission.view_installations,
            permissionType: 'sitePermissions' //This is still called 'sitePermissions' in the userInformation endpoint, but it is actually installation permission
          }}
        >
          <Layout><AssetDetails /></Layout>
        </AuthGuard>
    },
    {
      path: '/quotelist',
      element:
        <AuthGuard
          requiredPermission={{
            permission: QuotePermission.view_quotes,
            permissionType: 'quotePermissions'
          }}
        >
          <Layout><QuoteListPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/quoteDetails',
      element:
        <AuthGuard
          requiredPermission={{
            permission: QuotePermission.view_quotes,
            permissionType: 'quotePermissions'
          }}
        >
          <Layout><QuoteDetails /></Layout>
        </AuthGuard>
    },
    {
      path: '/invoicelist',
      element:
        <AuthGuard
          requiredPermission={{
            permission: InvoicePermission.view_invoices,
            permissionType: 'invoicePermissions'
          }}
        >
          <Layout><InvoiceListPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/discrepancyList',
      element:
        <AuthGuard
          requiredPermission={{
            permission: DiscrepancyPermission.view_discrepancy,
            permissionType: 'discrepancyPermissions'
          }}
        >
          <Layout><DiscrepancyListPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/discrepancyDetails',
      element:
        <AuthGuard
          requiredPermission={{
            permission: DiscrepancyPermission.view_discrepancy,
            permissionType: 'discrepancyPermissions'
          }}
        >
          <Layout><DiscrepancyDetails /></Layout>
        </AuthGuard>
    },

    {
      path: '/companies',
      element:
        <AuthGuard isSuperAdmin={true} >
          <Layout><CompaniesList /></Layout>
        </AuthGuard>
    },
    {
      path: '/companyDetails',
      element:
        <AuthGuard isSuperAdmin={true} >
          <Layout><CompanyDetails /></Layout>
        </AuthGuard>
    },
    {
      path: '/permissions',
      element:
        <AuthGuard isSuperAdmin={true} >
          <Layout><Permissions /></Layout>
        </AuthGuard>
    },
    {
      path: '/activity',
      element:
        <AuthGuard isSuperAdmin={true} >
          <Layout><Activity /></Layout>
        </AuthGuard>
    },
    {
      path: '/users',
      element:
        <AuthGuard
          requiredPermission={{
            permission: IdentityPermission.view_identities,
            permissionType: 'identityPermissions'
          }}
        >
          <Layout><UsersPage /></Layout>
        </AuthGuard>
    },
    {
      path: '/userDetails',
      element:
        <AuthGuard
          requiredPermission={{
            permission: IdentityPermission.view_identities,
            permissionType: 'identityPermissions'
          }}
          isSuperAdmin={true}
        >
          <Layout><UserDetails /></Layout>
        </AuthGuard>
    },
    {
      path: '/login',
      element: <Login />
    },
    {
      path: '/logout',
      element: <Logout />
    },
    {
      path: '/profile',
      element: <Profile />
    },
    {
      path: '/change-password',
      element: <ChangePassword />
    },
    {
      path: '/access-denied',
      element: <AccessDeniedPage />
    },
    {
      path: '/not-found',
      element: <NotFoundPage />
    },
    {
      path: '/error',
      element: <Layout><SomethingWentWrongPage /></Layout>
    },
    {
      path: '*',
      element: <Navigate to="/not-found" replace />
    }
  ];

  return useRoutes(routes);
}

const LandingPage = Loadable(lazy(() => import('../components/landing-page/LandingPage')));

const OrderListPage = Loadable(lazy(() => import('../components/order-list/OrderListPage')));

const QuoteListPage = Loadable(lazy(() => import('../components/quote-list/QuoteListPage')));

const QuoteDetails = Loadable(lazy(() => import('../components/quoteDetails/QuoteDetails')));

const InvoiceListPage = Loadable(lazy(() => import('../components/invoice-list/InvoiceListPage')));

const DiscrepancyListPage = Loadable(lazy(() => import('../components/discrepancyList/DiscrepancyList')));

const DiscrepancyDetails = Loadable(lazy(() => import('../components/discrepancyDetails/DiscrepancyDetails')));

const Booking = Loadable(lazy(() => import('../components/create-order/Booking')));

const AssetList = Loadable(lazy(() => import('../components/AssetList')));

const OrderDetails = Loadable(lazy(() => import('../components/order-details/OrderDetails')));

const AssetDetails = Loadable(lazy(() => import('../components/assetDetails/AssetDetails')));

const ActivityLog = Loadable(lazy(() => import('../components/activity-log/ActivityLog')));

const CompaniesList = Loadable(lazy(() => import('../components/userSettings/CompaniesList')));

const CompanyDetails = Loadable(lazy(() => import('../components/userSettings/CompanyDetails')));

const Permissions = Loadable(lazy(() => import('../components/userSettings/Permissions')));

const Activity = Loadable(lazy(() => import('../components/userSettings/Activity')));

const UsersPage = Loadable(lazy(() => import('../components/userSettings/UsersPage')));

const UserDetails = Loadable(lazy(() => import('../components/userSettings/UserDetails')));

const Login = Loadable(lazy(() => import('../components/authorization/Login')));

const Logout = Loadable(lazy(() => import('../components/authorization/Logout')));

const AccessDeniedPage = Loadable(lazy(() => import('../pages/generalPages/AccessDeniedPage')));

const NotFoundPage = Loadable(lazy(() => import('../pages/generalPages/Page404')));

const SomethingWentWrongPage = Loadable(lazy(() => import('../pages/generalPages/SomethingWentWrongPage')));

